import { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { isObject, keyCountInObject, objectValuesToList } from '../Util';
import { siteboardAction, setPath, setNewScheduleDialogueId, setPublishScheduleDialogueId, } from '../actions/defaultActions';
import { UUID, DateStringMMDDYYYY, ADD_SCHEDULE, SET_WELCOME_DIALOGUE_OPEN, Schedule, SET_ALERT, SET_EDIT_PROJECT_DIALOGUE_ID, } from '../actions/types';
import { format } from 'date-fns';

import PopupWindow from './PopupWindow';
import CalendarPicker from './CalendarPicker';
import DatePicker from './DatePicker';

import CheckboxIcon from '../icons/checkboxIcon';
import PushButton from './PushButton';
import SendIcon from '../icons/sendIcon';
import AddIcon from '../icons/addIcon';

const NewScheduleDialogue = ({ _projects, _schedules, _newScheduleDialogueProjectId, _newScheduleDialogueScheduleId, setNewScheduleDialogueId, siteboardAction, setPath, setPublishScheduleDialogueId }) => {

  const projectSchedules = [];
  for(const key in _schedules) {
    if(_schedules[key].projectId === _newScheduleDialogueProjectId) {
      projectSchedules.push(_schedules[key]);
    }
  }

  const selectedProject = _projects[_newScheduleDialogueProjectId];
  const selectedSchedule = _schedules[_newScheduleDialogueScheduleId];
  const latestScheduleDateString = (() => {
    if(!selectedProject) return null;
    const scheduleDates = objectValuesToList(selectedProject.schedules).map((dateString) => DateStringMMDDYYYY.toDateObject(dateString).getTime());
    if(scheduleDates.length === 0) return null;
    scheduleDates.sort((one, two) => { return two - one; });
    return format(new Date(scheduleDates[0]), 'MM/dd/yyyy');
  })();
  const isLatestSchedule = selectedSchedule ? selectedSchedule.beginDate === latestScheduleDateString : false;
  const startOnDate = (() => {
    if(!selectedProject) return format(new Date(), 'MM/dd/yyyy');
    const scheduleDates = objectValuesToList(selectedProject.schedules).map((dateString) => DateStringMMDDYYYY.toDateObject(dateString).getTime());
    if(scheduleDates.length === 0) {
      // start the schedule on the project start date
      return DateStringMMDDYYYY.isValid(selectedProject.beginDate) ? selectedProject.beginDate : format(new Date().getTime(), 'MM/dd/yyyy');
    }
    scheduleDates.sort((one, two) => { return two - one; });
    return convertDatestringPlus7Days(format(new Date(scheduleDates[0]), 'MM/dd/yyyy')); // latest schedule date + 7 days
  })();

  const [beginDate, setBeginDate] = useState(startOnDate);
  const [inputCopyFromLastSchedule, setInputCopyFromLastSchedule] = useState(projectSchedules.length > 0);
  const inputDateRef = useRef(null);

  // focus input when component is shown
  useEffect(() => {
    //try { inputDateRef.current.focus(); } catch(error) {}
  }, []);

  if(typeof selectedProject === 'undefined') return (<></>);

  const closeDialogue = () => {
    setNewScheduleDialogueId(null, null);
  };

  const submitDialogue = () => {
    try {
      const date = DateStringMMDDYYYY.toDateObject(beginDate, '"Start Date"');
      const isCopyingFromLastSchedule = inputCopyFromLastSchedule === true && UUID.isValid(_newScheduleDialogueScheduleId);
      const copyFromSchedule = (isCopyingFromLastSchedule && _schedules[_newScheduleDialogueScheduleId] !== undefined) ? _schedules[_newScheduleDialogueScheduleId] : null;

      const onSuccess = (output) => {
        closeDialogue();
        setPath('projectId#' + output.schedule.projectId + '#scheduleId#' + output.schedule.id); 
        siteboardAction(SET_WELCOME_DIALOGUE_OPEN, { isOpen: false }); // in case this is used from the welcome screen during onboarding. maybe close welcome another way 
      };
      if(copyFromSchedule !== null) {
        siteboardAction(ADD_SCHEDULE, {
          subscriptionId: selectedProject.subscriptionId,
          schedule: {
            id: '00000000-0000-0000-0000-000000000000',
            projectId: selectedProject.id,
            beginDate: beginDate,
            weatherDates: {...copyFromSchedule.weatherDates},
            holidayDates: {...copyFromSchedule.holidayDates},
            generalNotes: copyFromSchedule.generalNotes,
          },
          isCopyingFromSchedule: true,
          copyFromSchedulId: copyFromSchedule.id,
        }, onSuccess);
      } else {
        siteboardAction(ADD_SCHEDULE, {
          subscriptionId: selectedProject.subscriptionId,
          schedule: {
            id: '00000000-0000-0000-0000-000000000000',
            projectId: selectedProject.id,
            beginDate: beginDate,
          },
          isCopyingFromSchedule: false,
          copyFromSchedulId: '00000000-0000-0000-0000-000000000000',
        }, onSuccess);
      }
    } catch(error) {
      siteboardAction(SET_ALERT, { message: error.message }); 
    }
  }

  return (
    <PopupWindow title={`New 3-Week`} closeDialogue={closeDialogue} bgStyle='bg-neutral-800'>
      <div className='max-w-screen-sm text-white'>

        {/* filler to make the window expand to its max width */}
        <div className='h-0 w-[90vw] max-w-full'></div>

        <div className='flex flex-col gap-y-4'>

          {/* Ask to publish schedule if not published */}
          {selectedSchedule && !selectedSchedule.isPublished && isLatestSchedule &&
            <div className={`flex flex-col justify-start items-start gap-2 bg-neutral-900 p-6 rounded-lg`}>
              Do you want to publish your schedule before creating a new one?
              <PushButton 
                textColor='black'
                bgColor='#f9a928'
                hoverBgColor='#f79800'
                onClick={() => {
                  setPublishScheduleDialogueId(selectedSchedule.id);
                }}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <SendIcon size={16} color={'#000'} />
                  <p className=''>Publish & Send</p>
                </div>
              </PushButton>
            </div>
          }

          <div className='flex flex-col gap-1'>
            {keyCountInObject(selectedProject.schedules) === 0
              ? <div>Choose the beginning date for your first <b className=''>{selectedProject.name}</b> schedule.</div>
              : <div>Choose the beginning date for your updated schedule.</div>
            }
          </div>

          {/* begin date */}
          {/*<div className='w-full' >
            <label className='flex text-sm font-medium mb-1 gap-x-2'><p>Start Date</p><p className='opacity-50'>(mm/dd/year)</p></label>
            <input 
              ref={inputDateRef}
              className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
              type='text' 
              value={beginDate}
              onChange={(event) => { setBeginDate(event.target.value); }}
              onKeyUp={(event) => { if(event.key === 'Enter') { submitDialogue(); } }}
            />
          </div>*/}

          {/*
          <div className='w-full' >
            <div className='h-[300px]'>
              <CalendarPicker selectedDate={beginDate} onDateChange={(dateString) => { setBeginDate(dateString); }} onSubmit={submitDialogue} />
            </div>
          </div>
          <div className='text-center'><span className='font-normal'>Selected Start Date:</span> <b>{format(beginDate, 'iiii, MMMM dd, yyyy')}</b></div>

          projectSchedules.length > 0 &&
            <div className='flex flex-col items-start'>
              <div className='w-full flex justify-between'>
                <button 
                  className='w-full flex justify-between items-end gap-x-4'
                  onClick={() => { setInputCopyFromLastSchedule(!inputCopyFromLastSchedule); }}
                >
                  <div className='relative top-[-5px]'>Retain current schedule's info</div>
                  <div className='relative top-[-10px] border-b border-dashed border-white grow opacity-70'></div>
                  <CheckboxIcon isChecked={inputCopyFromLastSchedule} color='#fff' />
                </button>
              </div>
              <div className='w-full'>
                <button 
                  className='w-full flex justify-between items-end gap-x-4'
                  onClick={() => { setInputCopyFromLastSchedule(!inputCopyFromLastSchedule); }}
                >
                  <div className='relative top-[-5px]'>Create blank schedule</div>
                  <div className='relative top-[-10px] border-b border-dashed border-white grow opacity-70'></div>
                  <CheckboxIcon isChecked={!inputCopyFromLastSchedule} color='#fff' />
                </button>
              </div>
            </div>
          */}

          <div className='flex flex-col gap-1'>
            <DatePicker
              label="Schedule start date"
              value={DateStringMMDDYYYY.toDateObject(beginDate).getTime()}
              onChange={(value) => { setBeginDate(format(value, 'MM/dd/yyyy')); }}
            />

            <ScheduleInfoToggle 
              retainScheduleInfo={inputCopyFromLastSchedule}
              onChange={(value) => {
                setInputCopyFromLastSchedule(value);
              }}
            />
          </div>

          {/* Create 3-week button */}
          <div className='flex flex-col gap-1'>
            <div className='w-full flex flex-col justify-center'>
              {/* save button  */}
              <div className='w-full flex justify-center'>
                <PushButton 
                  textColor='white'
                  bgColor='#471cff'
                  hoverBgColor='#603aff'
                  onClick={() => { submitDialogue(); }}
                >
                  <div className='flex items-center gap-x-[6px]'>
                    <AddIcon color='#fff' />
                    <div>Create 3-Week Schedule</div>
                  </div>
                </PushButton>
              </div>
            </div>
            {projectSchedules.length > 0 &&
              <div className='flex justify-center w-full px-4 opacity-70'>Your current schedule will be saved for future reference.</div>
            }
          </div>

        </div>
      </div>
    </PopupWindow>
  );
};

const convertDatestringPlus7Days = (dateString) => { // convert a mm/dd/yyyy datestring to move it forward 7 days
  try {
    const dateObject = DateStringMMDDYYYY.toDateObject(dateString);
    dateObject.setTime(dateObject.getTime()+(1000*60*60*24*7)); // add 7 days to start time
    return format(dateObject, 'MM/dd/yyyy'); // return ie "Jan 02, 2023" format string, if there's a parse error return the error message
  } catch(error) {
    return '';
  }
};

const ScheduleInfoToggle = ({
  retainScheduleInfo = true,
  onChange,
}) => {
  const options = [
    {
      value: true,
      label: "Retain current schedule's info",
      description: "Start with your existing schedule details.",
    },
    {
      value: false,
      label: "Create blank schedule",
      description: "Start fresh with an empty schedule.",
    },
  ];

  return (
    <div
      role="group"
      aria-label="New schedule starting point"
      className="grid w-full grid-cols-1 gap-2 rounded-xl bg-neutral-900/50 p-1.5 sm:grid-cols-2"
    >
      {options.map((option) => {
        const isSelected = retainScheduleInfo === option.value;

        return (
          <button
            key={String(option.value)}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={`
              flex items-start gap-3 rounded-lg border p-3 text-left
              transition-colors duration-200
              focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-[#f9a928] focus-visible:ring-offset-2
              focus-visible:ring-offset-neutral-900
              ${
                isSelected
                  ? "border-[#f9a928]/50 bg-[#f9a928]/10"
                  : "border-transparent bg-neutral-900 text-white/70 hover:bg-neutral-800"
              }
            `}
          >
            <span
              aria-hidden="true"
              className={`
                mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center
                rounded-full border transition-colors duration-200
                ${
                  isSelected
                    ? "border-[#f9a928] bg-[#f9a928]"
                    : "border-neutral-600"
                }
              `}
            >
              <span
                className={`
                  h-2 w-2 rounded-full bg-neutral-900
                  transition-transform duration-200
                  ${isSelected ? "scale-100" : "scale-0"}
                `}
              />
            </span>

            <span className="flex min-w-0 flex-col gap-1">
              <span
                className={`font-semibold leading-snug ${
                  isSelected ? "text-[#f9a928]" : "text-white/90"
                }`}
              >
                {option.label}
              </span>

              <span className="leading-snug text-white/50">
                {option.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _newScheduleDialogueProjectId: state.localEphemeral.newScheduleDialogueProjectId,
  _newScheduleDialogueScheduleId: state.localEphemeral.newScheduleDialogueScheduleId,
}};
export default connect(mapStateToProps, {siteboardAction, setPath, setNewScheduleDialogueId, setPublishScheduleDialogueId})(NewScheduleDialogue);
